<script lang="ts">
export default {
  name: 'PublishPolicyModal',
};
</script>

<script setup lang="ts">
import AppModal from '@/components/v2/AppModal.vue';
import { policyOverrideService } from '@/services/api';
import { AppState, Commit } from '@/store';
import { SYNC_TOWER_LIBRARY_PANE, PUBLISH_POLICY } from '@/store/CustomEvents';
import { CurrentPolicyLineOverride, PolicyLineOverride, PolicyOverride } from '@/types/PolicyOverrides';
import { CurrentPolicyOverride } from '@/types/PolicyOverrides';
import { PolicyType } from '@/types/Programs';
import { NONE } from '@/types/SideATypes';
import { StoreMutationType } from '@/types/StoreMutationType';
import { PolicySourceManual } from '@/utilities/PolicySources';
import { ProgramPolicyItem } from '@/views/tower/v2/objects/ProgramsLibraryPanel';
import { onBeforeMount } from 'vue';
import { useStore } from 'vuex';

const props = defineProps<{
  policy?: ProgramPolicyItem;
  policyType?: PolicyType;
}>();

const emit = defineEmits<{
  close: [];
  publish: [];
}>();

const store = useStore<AppState>();

// -----------
// lifecycles
// -----------

onBeforeMount(() => {
  if (!props.policy || !props.policyType || props.policyType === 'custom-policy') {
    handleClose();
  }
});

function handleClose() {
  Commit(StoreMutationType.EDIT_PUBLISH_TOWER_MODAL_STATE, {
    show: false,
  });
  emit('close');
}

function handlePublishConfirm() {
  Commit(StoreMutationType.EDIT_PUBLISH_TOWER_MODAL_STATE, {
    show: false,
  });

  if (props.policyType === 'policy') {
    savePolicyOverride();
  } else if (props.policyType === 'policy-line') {
    savePolicyLineOverride();
  }

  const activeObj = store.state.towerCanvasState.activeCanvasObject;
  if (activeObj) {
    activeObj.policy = props.policy;
    activeObj.policy!.diffFields = [];
    activeObj.policy!.touchedFields = [];
    Commit(StoreMutationType.SET_ACTIVE_CANVAS_OBJECT, activeObj);
    activeObj?.canvas?.requestRenderAll();
  }

  dispatchEvent(new CustomEvent(PUBLISH_POLICY, { detail: props.policy }));
  dispatchEvent(new CustomEvent(SYNC_TOWER_LIBRARY_PANE));
  emit('publish');
}

async function savePolicyOverride(): Promise<void> {
  if (!props.policy) return;
  const policyOverrides: PolicyOverride = {} as PolicyOverride;

  policyOverrides.peFlag = resolveStringField(props.policy?.p_e_flag ?? '') || '';
  policyOverrides.attachmentOverride = resolveNumberField(props.policy?.attachmentPointValue?.toString() || '');
  policyOverrides.noAttachment = props.policy?.noAttachment;
  policyOverrides.aggregateLimitOverride = resolveNumberField(props.policy?.aggregateLimit?.toString() || '');
  policyOverrides.noLimit = props.policy?.noLimit;
  policyOverrides.occurrenceLimitOverride = resolveNumberField(props.policy?.occurrenceLimit?.toString() || '');
  policyOverrides.noOccurrenceLimit = props.policy?.noOccurrenceLimit;
  policyOverrides.retentionOverride = resolveNumberField(props.policy?.retention?.toString() || '');
  policyOverrides.noRetention = props.policy?.noRetention;
  policyOverrides.quotaShareTotalOverride = resolveNumberField(props.policy?.quotaShareTotal?.toString() ?? '');
  policyOverrides.sideAOverride = props.policy?.sideA !== NONE ? (props.policy?.sideA ?? '') : '';
  policyOverrides.runoffOverride = props.policy?.runoff === 'Runoff';
  policyOverrides.syndicateId = props.policy?.syndicateId === null ? undefined : props.policy?.syndicateId;
  policyOverrides.policyPremiumOverride = resolveNumberField(props.policy?.policyPremium?.toString() || '');
  policyOverrides.carrierOverride = resolveStringField(props.policy?.carrier ?? '');

  if (props.policy?.policySourceId !== PolicySourceManual) {
    policyOverrides.policyPremiumOverride = undefined;
    policyOverrides.carrierOverride = undefined;
  }

  // save policy override
  await policyOverrideService.SetOverride(props.policy.id.toString(), { policyOverride: policyOverrides } as CurrentPolicyOverride);
}

async function savePolicyLineOverride(): Promise<void> {
  if (!props.policy) return;
  const policyLineOverrides: PolicyLineOverride = {} as PolicyLineOverride;

  policyLineOverrides.attachmentOverride = resolveNumberField(props.policy?.attachmentPointValue?.toString() || '');
  policyLineOverrides.noAttachment = props.policy?.noAttachment;
  policyLineOverrides.aggregateLimitOverride = resolveNumberField(props.policy?.aggregateLimit?.toString() || '');
  policyLineOverrides.noLimit = props.policy?.noLimit;
  policyLineOverrides.occurrenceLimitOverride = resolveNumberField(props.policy?.occurrenceLimit?.toString() || '');
  policyLineOverrides.noOccurrenceLimit = props.policy?.noOccurrenceLimit;
  policyLineOverrides.retentionOverride = resolveNumberField(props.policy?.retention?.toString() || '');
  policyLineOverrides.noRetention = props.policy?.noRetention;
  policyLineOverrides.quotaShareTotalOverride = resolveNumberField(props.policy?.quotaShareTotal?.toString() ?? '');
  policyLineOverrides.policyPremiumOverride = resolveNumberField(props.policy?.policyPremium?.toString() || '');

  // save policy line override
  await policyOverrideService.SetLineOverride(props.policy.id.toString(), props.policy.policyLineId!.toString(), {
    policyOverride: policyLineOverrides,
  } as CurrentPolicyLineOverride);
}

function resolveStringField(str: string, defaultVal?: string): string | undefined {
  return str ? str : defaultVal || undefined;
}

function resolveNumberField(str: string): number | undefined {
  return str ? Number.parseFloat(str) : undefined;
}
</script>

<template>
  <AppModal
    :show="true"
    size="lg"
    action-button-label="Confirm"
    :handle-cancel="handleClose"
    :handle-action="handlePublishConfirm">
    <template #title>
      <div class="flex flex-col">
        <h1 class="pb-2 text-2xl">Confirm Policy Updates</h1>
        <span class="pb-2 text-xs font-normal break-normal text-red-600 italic">
          *This data will be updated throughout the Platform. Benchmarking, carrier analytics, and client data will be altered.
        </span>
      </div>
    </template>
    <template #content>
      <div class="overflow-x-auto">
        <table class="table border-collapse table-zebra">
          <thead class="font-semibold text-black">
            <tr>
              <th>Field</th>
              <th>Current</th>
              <th>New</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(field, index) in policy?.diffFields"
              :key="index">
              <td>{{ field.fieldLabel }}</td>
              <td class="text-red-700">{{ field.currentValue || '--' }}</td>
              <td class="text-green-700">{{ field.newValue || '--' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </AppModal>
</template>
